import React, { useContext } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as TooltipChart,
  Legend,
  Title,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import LayersIcon from '@mui/icons-material/Layers';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';

import { IndicatorContainer } from './IndicatorContainer';
import { LayerListContainer } from './LayerListContainer';
import { useResponsive } from '../hooks/useResponsive';
import { ExploreContext } from '../context/ExploreContext';

ChartJS.register(ArcElement, TooltipChart, Legend, Title, ChartDataLabels);

export const InfoContainer = () => {
  const { t } = useTranslation();

  const { isMobile, isDesktop } = useResponsive();
  const [openTooltipExpand, setOpenTooltipExpand] = React.useState(false);

  const {
    drawnLength,
    describedLength,
    theoreticalLength,
    walkLength,
    walkCycleLength,
    crossPointNumber,
    crossPathNumber,
    layerConfig,
    handleSetLayer,
    valueShown,
    interval,
    handleInterval,
    expand,
    setExpand,
    content,
    setContent,
    noRequest,
  } = useContext(ExploreContext);

  const handleSelect = (evt) => {
    handleInterval(evt.target.value);
  };

  const style = {
    container: {
      display: 'flex',
      width: '100%',
      flexDirection: 'column',
    },
    stackContainer: {
      display: 'flex',
      width: '100%',
      alignItems: 'center',
    },
    buttonContainer: {
      display: 'flex',
      width: '100%',
      justifyContent: isDesktop ? 'space-evenly' : 'center',
      position: isDesktop ? null : 'fixed',
      backgroundColor: '#FFFFFF',
      paddingY: '.5em',
      zIndex: 100,
      marginTop: isDesktop ? null : '-2px',
      alignItems: 'center',
    },
    groupContainer: {
      display: 'flex',
    },
    loadingContainer: {
      display: 'flex',
      paddingTop: isDesktop ? null : '5em',
      height: '100%',
    },
    contentContainer: {
      display: 'flex',
      width: '100%',
      justifyContent: 'center',
      paddingTop: isDesktop ? null : '1em',
      overflow: 'scroll',
      '&::-webkit-scrollbar': {
        width: '0.4em',
      },
      '&::-webkit-scrollbar-track': {
        width: '0.2em',
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: '0.5em',
        backgroundColor: 'gray',
      },
    },
    selectContainer: {
      display: 'flex',
      width: '180px',
    },
    buttonContent: {
      width: '9em',
      fontSize: '0.8rem',
    },
    buttonIconContent: {
      width: '3em',
      fontSize: '1rem',
      border: '1px solid #A9A9A9',
      borderRadius: '10px',
    },
    buttonIconContentSelected: {
      width: '3em',
      fontSize: '1rem',
      border: '1px solid',
      color: '#FFFFFF',
      backgroundColor: '#1976d2',
      borderRadius: '10px',
      '&:focus': {
        backgroundColor: '#1976d2',
        opacity: 0.8,
      },
    },
    controlContainer: {
      display: 'flex',
      width: '95%',
      justifyContent: 'space-between',
      paddingY: '1em',
    },
    expandContainer: {
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      alignSelf: 'flex-end',
    },
    labelExpand: {
      fontSize: '.8em',
      marginLeft: 1,
    },
    iconExpand: {
      color: '#797676',
      width: '1.8em',
      height: '2.5em',
      borderRadius: '10px',
      display: 'flex',
      textTransform: 'none',
      fontSize: '1em',
      '&: hover': {
        textDecoration: 'underline',
        backgroundColor: '#A9A9A9',
      },
    },
  };

  const renderResults = () => {
    if (content === 'indicator') {
      if (expand) {
        return (
          <Box sx={style.contentContainer}>
            <IndicatorContainer
              drawnLength={drawnLength}
              valueShown={valueShown}
              theoreticalLength={theoreticalLength}
              describedLength={describedLength}
              walkLength={walkLength}
              walkCycleLength={walkCycleLength}
              pointCrossNumber={crossPointNumber}
              pathCrossNumber={crossPathNumber}
              interval={interval}
            />
          </Box>
        );
      } else {
        return;
      }
    }
    if (content === 'layer') {
      if (expand) {
        return (
          <Box sx={style.contentContainer}>
            <LayerListContainer
              layerConfig={layerConfig}
              handleLayer={handleSetLayer}
            />
          </Box>
        );
      }
    }
  };

  return (
    <Stack sx={style.stackContainer} direction='column' spacing={3}>
      {noRequest ? (
        <Box sx={{ display: 'flex', padding: '1em' }}>
          {t('sidebar.enter_data')}
        </Box>
      ) : (
        <Box sx={style.controlContainer}>
          <Stack
            sx={style.groupContainer}
            direction='row'
            spacing={isMobile ? 1 : 2}
          >
            {isDesktop && (
              <Tooltip
                open={openTooltipExpand}
                arrow
                title={
                  <Typography fontSize={isDesktop ? null : '1.4em'}>
                    {expand
                      ? t('sidebar.collapse_label')
                      : t('sidebar.expand_label')}
                  </Typography>
                }
              >
                <IconButton
                  sx={style.iconExpand}
                  size='medium'
                  onMouseEnter={() => setOpenTooltipExpand(true)}
                  onMouseLeave={() => setOpenTooltipExpand(false)}
                  onClick={() => {
                    setOpenTooltipExpand(false);
                    setExpand(!expand);
                  }}
                >
                  {expand ? <ChevronRight /> : <ChevronLeft />}
                </IconButton>
              </Tooltip>
            )}
            {expand && !isMobile && (
              <Button
                sx={style.buttonContent}
                size={isMobile ? 'small' : 'medium'}
                startIcon={<DragIndicatorIcon />}
                variant={content === 'indicator' ? 'contained' : 'outlined'}
                onClick={() => setContent('indicator')}
              >
                {t('sidebar.indicator')}
              </Button>
            )}
            {expand && isMobile && (
              <IconButton
                sx={
                  content === 'indicator'
                    ? style.buttonIconContentSelected
                    : style.buttonIconContent
                }
                size='medium'
                autoFocus={false}
                onClick={() => setContent('indicator')}
              >
                <DragIndicatorIcon />
              </IconButton>
            )}
            {expand && !isMobile && (
              <Button
                sx={style.buttonContent}
                size={isMobile ? 'small' : 'medium'}
                startIcon={<LayersIcon />}
                variant={content === 'layer' ? 'contained' : 'outlined'}
                onClick={() => setContent('layer')}
              >
                {t('sidebar.layer')}
              </Button>
            )}
            {expand && isMobile && (
              <IconButton
                sx={
                  content === 'layer'
                    ? style.buttonIconContentSelected
                    : style.buttonIconContent
                }
                size='medium'
                autoFocus={false}
                onClick={() => setContent('layer')}
              >
                <LayersIcon />
              </IconButton>
            )}
          </Stack>
          <Box sx={style.selectContainer}>
            <FormControl fullWidth size='small'>
              <InputLabel id='select-periode-label'>
                {t('sidebar.period_label')}
              </InputLabel>
              <Select
                labelId='select-periode-label'
                id='select-periode'
                value={interval}
                label={t('sidebar.period_label')}
                onChange={handleSelect}
                autoWidth={true}
              >
                <MenuItem value={0}>{t('sidebar.period_all')}</MenuItem>
                <MenuItem value={1}>{t('sidebar.period_1_week')}</MenuItem>
                <MenuItem value={2}>{t('sidebar.period_1_month')}</MenuItem>
                <MenuItem value={3}>{t('sidebar.period_6_month')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}
      {!noRequest && renderResults()}
    </Stack>
  );
};
